import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { documentsAPI, casesAPI } from '@/lib/api';
import { toast } from 'sonner';
import { useDropzone } from 'react-dropzone';
import { Upload, FileText, CheckCircle, AlertTriangle, ArrowLeft, Eye, Camera } from 'lucide-react';
import CameraCapture from '@/components/CameraCapture';

export default function ClientDocUpload() {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const [caseData, setCaseData] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCamera, setShowCamera] = useState(false);
  const [uploadStatus, setUploadStatus] = useState('idle'); // idle | uploading | success | error

  const fetchData = async () => {
    try {
      const [caseRes, docsRes] = await Promise.all([
        casesAPI.get(caseId),
        documentsAPI.list(caseId)
      ]);
      setCaseData(caseRes.data);
      setDocuments(docsRes.data);
    } catch (err) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [caseId]);

  // Check if document already exists
  const hasDocument = documents.length > 0;

  const handleUploadSuccess = (result) => {
    toast.success('Documento recebido. Iremos dar sequencia no processo.');
    // Redirect to dashboard after 1.5s
    setTimeout(() => {
      navigate('/client');
    }, 1500);
  };

  const onDrop = useCallback(async (acceptedFiles) => {
    if (acceptedFiles.length === 0) return;
    setUploadStatus('uploading');
    try {
      const res = await documentsAPI.upload(caseId, acceptedFiles[0]);
      setUploadStatus('success');
      handleUploadSuccess(res.data);
    } catch (err) {
      setUploadStatus('error');
      const detail = err.response?.data?.detail || 'Erro ao enviar documento';
      toast.error(detail);
    }
  }, [caseId, navigate]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
    },
    maxSize: 10 * 1024 * 1024,
    disabled: hasDocument || uploadStatus === 'uploading' || uploadStatus === 'success',
    multiple: false,
  });

  const handleCameraCapture = async (file) => {
    setShowCamera(false);
    setUploadStatus('uploading');
    try {
      const res = await documentsAPI.upload(caseId, file);
      setUploadStatus('success');
      handleUploadSuccess(res.data);
    } catch (err) {
      setUploadStatus('error');
      toast.error(err.response?.data?.detail || 'Erro ao enviar foto');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="rn-spinner" style={{ width: 32, height: 32 }} data-testid="loading-spinner" />
      </div>
    );
  }

  // === DOCUMENT ALREADY EXISTS — show simple message, no upload ===
  if (hasDocument) {
    const doc = documents[0];
    return (
      <div className="space-y-6" data-testid="client-doc-upload">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/client')} className="p-2 rounded-xl hover:bg-white/5 transition-colors">
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="font-display text-2xl font-semibold">Documentos</h1>
            <p className="text-sm text-white/50">{caseData?.docType}: {caseData?.docFormatted}</p>
          </div>
        </div>

        <div className="rn-card border-[rgba(0,255,163,0.2)]">
          <div className="text-center py-8">
            <CheckCircle size={56} className="mx-auto text-[var(--rn-green)] mb-4" />
            <h2 className="font-display text-lg font-semibold text-[var(--rn-green)] mb-2">
              Documento recebido.
            </h2>
            <p className="text-sm text-white/50">Iremos dar sequencia no processo.</p>
          </div>

          {/* Document info */}
          <div className="border-t border-white/10 pt-4 mt-2">
            <div className={`flex items-center gap-4 p-4 rounded-xl border ${
              doc.status === 'validado' ? 'bg-[rgba(0,255,163,0.03)] border-[rgba(0,255,163,0.15)]' :
              doc.status === 'divergente' ? 'bg-[rgba(239,68,68,0.03)] border-red-500/15' :
              doc.status === 'atencao' ? 'bg-[rgba(245,158,11,0.03)] border-[rgba(245,158,11,0.15)]' :
              'bg-white/[0.03] border-white/10'
            }`}>
              <FileText size={20} className="text-white/40" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{doc.fileName}</p>
                <div className="flex items-center gap-2 mt-1">
                  <StatusBadge status={doc.status} />
                  <OcrBadge validation={doc.ocrValidation} docId={doc.id} />
                </div>
              </div>
              <a
                href={documentsAPI.getFileUrl(doc.id)}
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 rounded-lg hover:bg-white/5 transition-colors"
              >
                <Eye size={16} className="text-white/60" />
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // === NO DOCUMENT YET — show upload area ===
  return (
    <div className="space-y-6" data-testid="client-doc-upload">
      {showCamera && (
        <CameraCapture onCapture={handleCameraCapture} onClose={() => setShowCamera(false)} />
      )}

      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/client')} className="p-2 rounded-xl hover:bg-white/5 transition-colors">
          <ArrowLeft size={20} />
        </button>
        <div>
          <h1 className="font-display text-2xl font-semibold">Enviar Documento</h1>
          <p className="text-sm text-white/50">{caseData?.docType}: {caseData?.docFormatted}</p>
        </div>
      </div>

      {/* Dropzone */}
      <div
        {...getRootProps()}
        className={`dropzone-area transition-all duration-300 ${
          isDragActive ? 'active' : ''
        } ${
          uploadStatus === 'uploading' ? 'opacity-50 cursor-not-allowed pointer-events-none' : ''
        } ${
          uploadStatus === 'success' ? '!border-[var(--rn-green)] !bg-[rgba(0,255,163,0.06)] opacity-50 pointer-events-none' : ''
        } ${
          uploadStatus === 'error' ? '!border-red-500/40 !bg-[rgba(239,68,68,0.06)]' : ''
        }`}
        data-testid="document-upload-dropzone"
      >
        <input {...getInputProps()} data-testid="document-upload-file-input" />

        {uploadStatus === 'idle' && (
          <>
            <Upload size={48} className="mx-auto text-[var(--rn-green)] opacity-60 mb-4" />
            <p className="text-lg font-medium mb-1">
              {isDragActive ? 'Solte para enviar' : 'Arraste e solte seu documento'}
            </p>
            <p className="text-sm text-white/50">Ou clique para selecionar (PDF, JPG, PNG)</p>
            <p className="text-xs text-white/30 mt-2">Tamanho maximo: 10 MB</p>
          </>
        )}

        {uploadStatus === 'uploading' && (
          <div className="flex flex-col items-center gap-3 py-4">
            <span className="rn-spinner" style={{ width: 32, height: 32 }} data-testid="loading-spinner" />
            <p className="text-lg font-medium text-white/80">Enviando...</p>
            <p className="text-sm text-white/50">Processando documento</p>
          </div>
        )}

        {uploadStatus === 'success' && (
          <div className="flex flex-col items-center gap-3 py-4">
            <CheckCircle size={48} className="text-[var(--rn-green)]" />
            <p className="text-lg font-medium text-[var(--rn-green)]">Documento recebido!</p>
            <p className="text-sm text-white/50">Redirecionando ao painel...</p>
          </div>
        )}

        {uploadStatus === 'error' && (
          <div className="flex flex-col items-center gap-3 py-4">
            <AlertTriangle size={48} className="text-red-400" />
            <p className="text-lg font-medium text-red-400">Erro no envio</p>
            <p className="text-sm text-white/50">Tente novamente</p>
          </div>
        )}
      </div>

      {/* Camera button — only if idle or error */}
      {(uploadStatus === 'idle' || uploadStatus === 'error') && (
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowCamera(true)}
            className="btn-secondary flex items-center gap-2"
            data-testid="camera-capture-button"
          >
            <Camera size={18} /> Usar Camera
          </button>
        </div>
      )}

      <p className="text-sm text-white/50">
        Envie uma foto legivel do seu documento com {caseData?.docType}.
      </p>
    </div>
  );
}

function StatusBadge({ status }) {
  if (status === 'validado') return <span className="badge-success">Validado</span>;
  if (status === 'atencao') return <span className="badge-warning">Recebido</span>;
  if (status === 'divergente') return <span className="badge-danger">Divergente</span>;
  if (status === 'approved') return <span className="badge-success">Validado</span>;
  return <span className="badge-neutral">Recebido</span>;
}

function OcrBadge({ validation, docId }) {
  if (validation === 'match') return <span className="badge-success" data-testid={`document-ocr-status-${docId}`}>OCR: Validado</span>;
  if (validation === 'mismatch') return <span className="badge-danger" data-testid={`document-ocr-status-${docId}`}>OCR: Divergente</span>;
  if (validation === 'inconclusive' || validation === 'failed') return <span className="badge-warning" data-testid={`document-ocr-status-${docId}`}>OCR: Inconclusivo</span>;
  return null;
}
